import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAccount, useConnect, useDisconnect, useChainId, useSwitchChain } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { SUPPORTED_NETWORKS, supportedChains, isSupportedChain } from '../config/wagmi';

const WagmiWeb3Context = createContext();

export const useWagmiWeb3 = () => {
  const context = useContext(WagmiWeb3Context);
  if (!context) {
    throw new Error('useWagmiWeb3 must be used within a WagmiWeb3Provider');
  }
  return context;
};

export const WagmiWeb3Provider = ({ children }) => {
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Wagmi hooks
  const { address, isConnected } = useAccount();
  const { isPending: connectPending } = useConnect();
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const { switchChain, isPending: switchPending } = useSwitchChain();
  // AppKit drives the unified EVM connect modal.
  const { open: openAppKit } = useAppKit();

  // Memoize so the value object doesn't churn every render — that churn was
  // making consumers' useEffect deps unstable and triggering an update loop.
  const network = useMemo(() => {
    if (!chainId) return null;
    const networkConfig = SUPPORTED_NETWORKS[chainId];
    if (!networkConfig) return null;
    return {
      chainId,
      name: networkConfig.name,
      shortName: networkConfig.shortName,
    };
  }, [chainId]);

  const isNetworkSupported = useMemo(
    () => !!(network && isSupportedChain(chainId)),
    [network, chainId],
  );

  // Open the AppKit modal — user picks any EVM wallet (MetaMask, Trust ext,
  // Trust mobile via WC QR, Rainbow, Coinbase, OKX, etc.). Wagmi state below
  // updates automatically once the user completes the connection.
  const connectWallet = useCallback(async () => {
    setError(null);
    try {
      await openAppKit({ view: 'Connect' });
    } catch (err) {
      console.error('Failed to open wallet modal:', err);
      setError(err?.message || 'Failed to open wallet modal');
    }
  }, [openAppKit]);

  // Disconnect wallet. Wagmi's `disconnect()` ends the active session, but
  // its persisted recent-connector keys can still trigger an auto-reconnect
  // on hard refresh. Clear them so the user's intent (disconnect) sticks.
  const disconnectWallet = useCallback(() => {
    try { disconnect(); } catch (e) { /* connector already gone */ }
    setError(null);

    try {
      localStorage.removeItem('walletConnected');
      localStorage.removeItem('connectedAccount');
      // Wagmi v2 storage keys (prefix is configurable but defaults to 'wagmi').
      const wipe = (store) =>
        Object.keys(store)
          .filter((k) => k.startsWith('wagmi.') || k.startsWith('@w3m/') || k.startsWith('@appkit/') || k.startsWith('@reown/'))
          .forEach((k) => store.removeItem(k));
      wipe(localStorage);
      if (typeof sessionStorage !== 'undefined') wipe(sessionStorage);
    } catch (e) {
      // ignore quota / disabled-storage errors
    }
  }, [disconnect]);

  // Switch network - but with restriction logic
  const switchNetwork = useCallback(async (targetChainId) => {
    if (!isSupportedChain(targetChainId)) {
      setError('Unsupported network');
      return false;
    }

    // Don't switch if already on the target network
    if (chainId === targetChainId) {
      return true;
    }

    setError(null);

    try {
      await switchChain({ chainId: targetChainId });
      return true;
    } catch (switchError) {
      console.error('Switch network error:', switchError);

      if (switchError.message?.includes('User rejected')) {
        setError('User rejected network switch');
      } else {
        setError('Failed to switch network');
      }
      return false;
    }
  }, [chainId, switchChain]);

  // Switches to whichever BSC network is configured for this build (97 testnet / 56 mainnet).
  const switchToBSC = useCallback(() => {
    const target = supportedChains[0]?.id;
    return target ? switchNetwork(target) : Promise.resolve(false);
  }, [switchNetwork]);

  // Check if MetaMask is installed
  const isMetaMaskInstalled = useCallback(() => {
    return typeof window !== 'undefined' &&
           typeof window.ethereum !== 'undefined' &&
           window.ethereum.isMetaMask;
  }, []);

  // Validate network access - restrict to current MetaMask network only
  const validateNetworkAccess = useCallback(async () => {
    if (!address || !network) return false;

    try {
      // Get current MetaMask network
      if (typeof window !== 'undefined' && window.ethereum) {
        const metamaskChainId = await window.ethereum.request({ method: 'eth_chainId' });
        const currentChainId = parseInt(metamaskChainId, 16);

        if (chainId !== currentChainId) {
          setError('Please switch to the correct network in MetaMask to continue.');
          return false;
        }
      }

      if (!isNetworkSupported) {
        setError('Current network is not supported. Please switch to a supported network in MetaMask.');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Network validation failed:', error);
      setError('Failed to validate network access.');
      return false;
    }
  }, [address, network, chainId, isNetworkSupported]);

  // Update connection status
  useEffect(() => {
    setIsConnecting(connectPending);
  }, [connectPending]);

  // Clear errors when account changes
  useEffect(() => {
    if (address) {
      setError(null);
      localStorage.setItem('connectedAccount', address);
    }
  }, [address]);

  const value = useMemo(() => ({
    // Account and connection info
    account: address,
    isConnected,
    network,
    chainId,

    // Connection state
    isConnecting: isConnecting || connectPending,
    isSwitchingNetwork: switchPending,
    error,

    // Network validation
    isNetworkSupported,
    isMetaMaskInstalled,
    validateNetworkAccess,

    // Actions
    connectWallet,
    disconnectWallet,
    switchNetwork,
    switchToBSC,

    // Configuration
    supportedNetworks: SUPPORTED_NETWORKS,

    // Wagmi hooks for advanced usage
    wagmiHooks: {
      useAccount,
      useConnect,
      useDisconnect,
      useChainId,
      useSwitchChain,
    },
  }), [
    address,
    isConnected,
    network,
    chainId,
    isConnecting,
    connectPending,
    switchPending,
    error,
    isNetworkSupported,
    isMetaMaskInstalled,
    validateNetworkAccess,
    connectWallet,
    disconnectWallet,
    switchNetwork,
    switchToBSC,
  ]);

  return (
    <WagmiWeb3Context.Provider value={value}>
      {children}
    </WagmiWeb3Context.Provider>
  );
};